import { useCallback, useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { ViewHeader } from './DashboardLayout';

/**
 * Dashboard → Billing.
 *
 * M21 T8: status-aware view of the tenant's app-subscription lifecycle.
 * Reads `GET /api/billing/status` (the Worker billing handler) and renders
 * the matching card: start trial / trial-in-progress / active / lapsed.
 * Self-service management (cancel, card update) opens the Stripe Customer
 * Portal via `POST /api/billing/portal-session`; starting/reactivating a
 * subscription goes through `POST /api/billing/checkout`.
 *
 * Pricing: flat $129/mo, 30-day Stripe-native trial, cancel any time.
 *
 * Reference: src/handlers/billing.ts (Worker endpoints)
 *            M21_PLAN.md T8
 */

interface BillingStatusResponse {
  status: 'none' | 'trialing' | 'active' | 'past_due' | 'incomplete' |
  'canceled' | 'unpaid' | 'incomplete_expired' | 'paused';
  hasSubscription: boolean;
  canManage: boolean;
  trialEndsAt: number | null;
  daysLeftInTrial: number | null;
  terminalAt: number | null;
  cancelAtPeriodEnd: boolean;
}

export function Billing() {
  const fetcher = useCallback(
    (signal: AbortSignal) =>
      api.get<BillingStatusResponse>('billing/status', { signal }),
    []
  );
  const { data, loading, error, refetch } = useAsync(fetcher, []);

  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const startCheckout = useCallback(async () => {
    setCheckoutLoading(true);
    try {
      const { url } = await api.post<{ url: string; }>('billing/checkout');
      window.location.href = url;
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  }, []);

  const openPortal = useCallback(async () => {
    setPortalLoading(true);
    try {
      const { url } = await api.post<{ url: string; }>('billing/portal-session');
      window.location.href = url;
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to open portal');
      setPortalLoading(false);
    }
  }, []);

  return (
    <>
      <ViewHeader
        title="Billing"
        subtitle="Your subscription to Stripe Sync by AmpDeck."
      />

      {loading && <p className="text-slate-500">Loading…</p>}
      {error && (
        <p className="text-red-600">
          {error.message}{' '}
          <button onClick={refetch} className="underline">
            Retry
          </button>
        </p>
      )}

      {data && (
        <BillingCard
          data={data}
          onStart={startCheckout}
          onManage={openPortal}
          portalLoading={portalLoading}
          checkoutLoading={checkoutLoading}
        />
      )}
    </>
  );
}

function BillingCard({
  data,
  onStart,
  onManage,
  portalLoading,
  checkoutLoading,
}: {
  data: BillingStatusResponse;
  onStart: () => void;
  onManage: () => void;
  portalLoading: boolean;
  checkoutLoading: boolean;
}) {
  const { status, daysLeftInTrial, canManage, cancelAtPeriodEnd } = data;

  // none → start-trial CTA
  if (status === 'none') {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900">Start your 30-day free trial</h3>
        <p className="mt-2 text-sm text-slate-600">
          $129/month after the trial. Cancel any time. Card required — you're a
          Stripe merchant, so this is the same flow your customers go through.
        </p>
        <button
          onClick={onStart}
          disabled={checkoutLoading}
          className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {checkoutLoading ? 'Redirecting…' : 'Start trial'}
        </button>
      </div>
    );
  }

  // trialing → days left + manage. Different message AND tone when the user
  // already scheduled a cancel (amber) vs. a healthy trial (green).
  if (status === 'trialing') {
    return (
      <div className={`rounded-lg border p-6 shadow-sm ${cancelAtPeriodEnd ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
        <h3 className={`text-lg font-semibold ${cancelAtPeriodEnd ? 'text-amber-900' : 'text-emerald-900'}`}>
          Trial active — {daysLeftInTrial} {daysLeftInTrial === 1 ? 'day' : 'days'} left
        </h3>
        <p className={`mt-2 text-sm ${cancelAtPeriodEnd ? 'text-amber-700' : 'text-emerald-700'}`}>
          {cancelAtPeriodEnd
            ? 'Your subscription is cancelled and will not renew. You keep access until the trial ends.'
            : '$129/month starts when the trial ends. Update your card or cancel any time.'}
        </p>
        {canManage && (
          <button
            onClick={onManage}
            disabled={portalLoading}
            className={`mt-4 rounded-md border bg-white px-4 py-2 text-sm font-medium disabled:opacity-50 ${cancelAtPeriodEnd ? 'border-amber-300 text-amber-700 hover:bg-amber-100' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-100'}`}
          >
            {portalLoading ? 'Opening portal…' : 'Manage billing'}
          </button>
        )}
      </div>
    );
  }

  // active / past_due / incomplete → live + manage
  if (status === 'active' || status === 'past_due' || status === 'incomplete') {
    const urgent = status !== 'active';
    // Cancel scheduled (via Portal) also renders amber even when 'active'.
    const warn = urgent || cancelAtPeriodEnd;
    return (
      <div className={`rounded-lg border p-6 shadow-sm ${warn ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
        <h3 className={`text-lg font-semibold ${warn ? 'text-amber-900' : 'text-slate-900'}`}>
          {status === 'active' && (cancelAtPeriodEnd ? 'Subscription ends soon' : 'Subscription active')}
          {status === 'past_due' && 'Payment overdue — please update your card'}
          {status === 'incomplete' && 'Payment processing'}
        </h3>
        <p className={`mt-2 text-sm ${warn ? 'text-amber-700' : 'text-slate-600'}`}>
          {status === 'active' && (cancelAtPeriodEnd
            ? 'Your subscription is cancelled and will not renew. You keep access until the current period ends.'
            : '$129/month. Manage your subscription in the portal.')}
          {status === 'past_due' && 'Your last payment failed. Update your card to avoid interruption.'}
          {status === 'incomplete' && 'Your initial payment is still being processed.'}
        </p>
        {canManage && (
          <button
            onClick={onManage}
            disabled={portalLoading}
            className={`mt-4 rounded-md border px-4 py-2 text-sm font-medium disabled:opacity-50 ${warn ? 'border-amber-300 bg-white text-amber-700 hover:bg-amber-100' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'}`}
          >
            {portalLoading ? 'Opening portal…' : 'Manage billing'}
          </button>
        )}
      </div>
    );
  }

  // terminal (canceled / unpaid / incomplete_expired / paused) → reactivate
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-rose-900">Subscription ended</h3>
      <p className="mt-2 text-sm text-rose-700">
        Sync continues to run during the post-cancel grace window, then your
        tenant is purged. Reactivate to keep everything running.
      </p>
      <button
        onClick={onStart}
        disabled={checkoutLoading}
        className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
      >
        {checkoutLoading ? 'Redirecting…' : 'Reactivate'}
      </button>
    </div>
  );
}
